import { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';

const { MenuRow } = MojitoPresentation.Components;
const IntentTypes = MojitoPresentation.Base.Intent.Types;
const intentActions = MojitoCore.Intents.actions;

const SportTabSelector = ({ routeParams, mojitoTools, shouldExcludeTab, tabs }) => {
    const { config, appContext, stringResolver } = mojitoTools;
    const { routeResolver, analyticsEmitter } = appContext;
    const dispatch = useDispatch();

    const items = getItems(
        tabs,
        routeParams,
        routeResolver,
        stringResolver,
        shouldExcludeTab,
        config.sportEventTypesOrder
    );
    const activeIndex = items.findIndex(item => appContext.isContentShown(item.intent.data));

    const { emitAnalytics } = analyticsEmitter;
    const onClick = useCallback(
        itemIndex => {
            const { intent, analyticsData } = items[itemIndex];
            const { type, ...restAnalyticsData } = analyticsData;
            emitAnalytics(type, restAnalyticsData);
            dispatch(intentActions.publishIntent(intent.type, intent.data));
        },
        [items, emitAnalytics, dispatch]
    );

    if (items.length < config.minAmountOfTabs) {
        return null;
    }

    return (
        <TabSelector
            config={config.tabs}
            items={items}
            onItemClicked={onClick}
            activeIndex={activeIndex}
        />
    );
};

const camelToUpperSnakeCase = str => str.replace(/[A-Z]/g, letter => `_${letter}`).toUpperCase();

const getSportBasedTranslation = (sportId, type, stringResolver) => {
    return (
        stringResolver.resolveString(`$SPORT_TAB_SELECTOR.SPORT.${type}.${sportId}`, true) ||
        stringResolver.resolveString(`$SPORT_TAB_SELECTOR.SPORT.${type}.DEFAULT`)
    );
};

const getItems = (
    tabs,
    routeParams,
    routeResolver,
    stringResolver,
    shouldExcludeTab,
    sportEventTypesOrder
) =>
    tabs
        .toSorted(
            (a, b) => sportEventTypesOrder.indexOf(a.name) - sportEventTypesOrder.indexOf(b.name)
        )
        .map(({ routeFn, name, analyticsData, className, icon }) => {
            const { sportId, typeId } = routeParams;
            const data = routeFn(sportId, typeId);

            if (shouldExcludeTab(name)) {
                return null;
            }

            return {
                label: getSportBasedTranslation(
                    sportId,
                    camelToUpperSnakeCase(name),
                    stringResolver
                ),
                intent: {
                    type: IntentTypes.VIEW,
                    data,
                },
                analyticsData: {
                    ...analyticsData,
                    sportId,
                    ...(typeId && { typeId }),
                },
                hrefLink: routeResolver.getRoute(data),
                className,
                icon,
            };
        })
        .filter(Boolean);

// eslint-disable-next-line react/display-name
const TabSelector = memo(({ items, activeIndex, onItemClicked, config }) => {
    const menuItems = items.map((item, index) => ({
        id: index,
        data: item.label,
        className: item.className,
        hrefLink: item.hrefLink,
        icon: item.icon,
    }));

    return (
        <MenuRow
            class={'ta-SportTabSelector'}
            config={config}
            menuItems={menuItems}
            selectedItemId={activeIndex}
            cbItemClicked={onItemClicked}
        />
    );
});

export default SportTabSelector;
